import getSymbolFromCurrency from "currency-symbol-map";
import React, { useState, useEffect } from "react";

// Currency conversion component for checkout page
const CurrencyConverter = ({ subtotal, shippingCost, totalPrice, onCurrencyChange }) => {
  const [currencies, setCurrencies] = useState([]);
  const [selectedCurrency, setSelectedCurrency] = useState("SAR");
  const [conversionRates, setConversionRates] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  
  // Fetch all currencies dynamically
  useEffect(() => {
    const fetchCurrencies = async () => {
      try {
        const res = await fetch(
          "https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies.json"
        );
        if (!res.ok) throw new Error("Failed to fetch currencies");
        
        const data = await res.json();

        const currencyList = Object.entries(data).map(([code, name]) => ({
          code: code.toUpperCase(),
          name: name.charAt(0).toUpperCase() + name.slice(1),
        }));

        // Sort currencies alphabetically
        currencyList.sort((a, b) => a.code.localeCompare(b.code));
        
        // Ensure SAR is at the top
        const sarIndex = currencyList.findIndex(curr => curr.code === "SAR");
        if (sarIndex > -1) {
          const sar = currencyList.splice(sarIndex, 1)[0];
          currencyList.unshift(sar);
        }

        setCurrencies(currencyList);
      } catch (err) {
        console.error("Error fetching currencies", err);
        setCurrencies([
          { code: "SAR", name: "Saudi Riyal" },
          { code: "USD", name: "US Dollar" },
          { code: "EUR", name: "Euro" },
          { code: "GBP", name: "British Pound" },
          { code: "PKR", name: "Pakistani Rupee" },
          { code: "INR", name: "Indian Rupee" },
          { code: "AED", name: "UAE Dirham" },
        ]);
      }
    };

    fetchCurrencies();
  }, []);

  // Fetch conversion rates when selectedCurrency changes
  useEffect(() => {
    if (selectedCurrency === "SAR") {
      setConversionRates({ SAR: 1 });
      onCurrencyChange("SAR", 1);
      return;
    }

    const fetchConversionRates = async () => {
      setIsLoading(true);
      setError("");

      try {
        const response = await fetch(
          `https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/sar.json`
        );
        
        if (!response.ok) throw new Error("Failed to fetch conversion rates");
        
        const data = await response.json();

        if (data && data.sar) {
          setConversionRates(data.sar);
          const rate = data.sar[selectedCurrency.toLowerCase()] || 1;
          onCurrencyChange(selectedCurrency, rate);
        } else {
          throw new Error("Invalid response structure");
        }
      } catch (err) {
        console.error("Error fetching conversion rates:", err);
        setError("Failed to fetch conversion rates");
        // fallback rates
        const fallbackRates = {
          sar: 1,
          usd: 0.27,
          eur: 0.24,
          gbp: 0.21,
          pkr: 75.51,
          inr: 22.42,
          aed: 0.98,
        };
        setConversionRates(fallbackRates);
        const fallbackRate = fallbackRates[selectedCurrency.toLowerCase()] || 1;
        onCurrencyChange(selectedCurrency, fallbackRate);
      } finally {
        setIsLoading(false);
      }
    };

    fetchConversionRates();
  }, [selectedCurrency, onCurrencyChange]);

  // Custom dropdown implementation
  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const selectCurrency = (currencyCode) => {
    setSelectedCurrency(currencyCode);
    setIsDropdownOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isDropdownOpen && !event.target.closest('.currency-dropdown')) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  // Get selected currency details
  const selectedCurrencyDetails = currencies.find(
    (c) => c.code === selectedCurrency
  ) || { code: "SAR", name: "Saudi Riyal" };

  return (
    <div className="mb-4 p-3 bg-gray-50 rounded-lg">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div className="text-sm font-medium text-gray-700">
          Currency Converter
          <p className="text-xs text-gray-500 mt-1">
            (View prices in different currencies)
          </p>
        </div>
        
        {/* Custom dropdown implementation */}
        <div className="relative currency-dropdown w-full sm:w-auto">
          <button
            type="button"
            onClick={toggleDropdown}
            disabled={isLoading}
            className="w-full sm:w-auto flex items-center justify-between px-4 py-2 border border-gray-300 rounded-md bg-white text-sm text-left focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            aria-haspopup="listbox"
            aria-expanded={isDropdownOpen}
          >
            <span>
              {selectedCurrencyDetails.code} - {selectedCurrencyDetails.name}
            </span>
            <svg className="h-4 w-4 ml-2" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>
          
          {isDropdownOpen && (
            <div className="absolute right-0 mt-1 w-full sm:w-64 bg-white border border-gray-300 rounded-md shadow-lg z-50 max-h-60 overflow-y-auto">
              <ul className="py-1" role="listbox">
                {currencies.map((currency) => (
                  <li
                    key={currency.code}
                    onClick={() => selectCurrency(currency.code)}
                    className={`px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 ${
                      selectedCurrency === currency.code ? "bg-blue-50 text-blue-700" : ""
                    }`}
                    role="option"
                    aria-selected={selectedCurrency === currency.code}
                  >
                    {getSymbolFromCurrency(currency.code)} - {currency.name}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {error && <p className="text-red-500 text-xs mt-2">{error}</p>}

      {selectedCurrency !== "SAR" && (
        <div className="text-xs text-gray-500 mt-2 italic">
          Note: The actual payment will be processed in ( {getSymbolFromCurrency('SAR')} ) SAR. This is only for reference.
        </div>
      )}
    </div>
  );
};

export default CurrencyConverter;